import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Plus, ChevronRight, AlertCircle } from 'lucide-react';
import { adminBusinesses, type Business } from '../../lib/api';

export default function AdminBusinesses() {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const [name, setName] = useState('');
  const [currency, setCurrency] = useState('GBP');
  const [fiscalMonth, setFiscalMonth] = useState(1);
  const [ceoEmail, setCeoEmail] = useState('');
  const [ceoName, setCeoName] = useState('');
  const [createErr, setCreateErr] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { businesses } = await adminBusinesses.list();
      setBusinesses(businesses);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateErr('');
    setCreateLoading(true);
    try {
      await adminBusinesses.create({ name, currency, fiscal_year_start_month: fiscalMonth, ceo_email: ceoEmail, ceo_full_name: ceoName });
      setShowCreate(false);
      setName(''); setCeoEmail(''); setCeoName('');
      load();
    } catch (err: any) {
      setCreateErr(err.message || 'Failed to create business');
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-green-600" /> Businesses
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">{businesses.length} onboarded</p>
        </div>
        <button onClick={() => setShowCreate(!showCreate)} className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" /> New Business
        </button>
      </div>

      {showCreate && (
        <div className="card border-green-200 bg-green-50 space-y-4">
          <h2 className="font-semibold text-gray-900">Create a Business</h2>
          {createErr && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{createErr}</div>}
          <form onSubmit={handleCreate} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="label">Business name <span className="text-red-500">*</span></label>
                <input type="text" className="input-field" value={name} onChange={e => setName(e.target.value)} required />
              </div>
              <div>
                <label className="label">Currency</label>
                <select className="input-field" value={currency} onChange={e => setCurrency(e.target.value)}>
                  <option value="GBP">GBP</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
              <div>
                <label className="label">Fiscal year start month</label>
                <select className="input-field" value={fiscalMonth} onChange={e => setFiscalMonth(Number(e.target.value))}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                    <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleDateString(undefined, { month: 'long' })}</option>
                  ))}
                </select>
              </div>
              <div />
              <div>
                <label className="label">CEO full name</label>
                <input type="text" className="input-field" value={ceoName} onChange={e => setCeoName(e.target.value)} />
              </div>
              <div>
                <label className="label">CEO email <span className="text-red-500">*</span></label>
                <input type="email" className="input-field" value={ceoEmail} onChange={e => setCeoEmail(e.target.value)} required />
              </div>
            </div>
            <p className="text-xs text-gray-500">The CEO will receive an email to set their password.</p>
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary">Cancel</button>
              <button type="submit" className="btn-primary" disabled={createLoading}>
                {createLoading ? 'Creating...' : 'Create Business'}
              </button>
            </div>
          </form>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 text-sm">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      <div className="card overflow-hidden p-0">
        {loading ? (
          <div className="p-8 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto" /></div>
        ) : businesses.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No businesses yet</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {businesses.map(b => (
              <Link key={b.id} to={`/admin/businesses/${b.id}`} className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors">
                <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-4 h-4 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">{b.name}</div>
                  <div className="text-sm text-gray-500">{b.currency} &bull; FY starts {new Date(2000, b.fiscal_year_start_month - 1, 1).toLocaleDateString(undefined, { month: 'long' })}</div>
                </div>
                {!b.is_active && <span className="bg-red-100 text-red-600 px-2 py-0.5 rounded-full text-xs font-medium">Inactive</span>}
                <ChevronRight className="w-4 h-4 text-gray-300" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
